import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, CAT } from '@/game/config';
import { getBackground, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { scriptFontFamily } from '@/game/fonts';
import { wordIconKey, wordIconUrl } from '@/game/wordIcons';
import { getLettersPack } from '@/data/packs/letters';
import { getPictureWordsPool } from '@/data/pools';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import {
  playPopSound, playThudSound, playGateSound, playRoundCompleteSound, playJourneyFanfare,
  speakRulePrompt, startBackdropAmbience, stopBackdropAmbience,
} from '@/game/audio';
import { classesOf, type LetterClass } from '@/game/cat/membership';
import { dealBoard, type CatBoard, type CatDoor } from '@/game/cat/deal';
import { cellKey, type Cell } from '@/game/cat/board';
import {
  step, lineSteps, doorSteps, tryDoor as doorOutcome, doorUnderCat, type WalkState,
} from '@/game/cat/walk';
import { renderTrain } from '@/game/cat/word';
import { letterProgress, recordBoard, activeClassIds, metClassIds, isKnown } from '@/game/cat/store';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface CatInit {
  lang: string;
  /** Dev only (`?cat=3`): start the run further up, where the two doors live. */
  startBoard?: number;
}

/**
 * Cat & Mouse — the Venus treat's letter hunt (specs/cat-mouse-mode.md).
 *
 * A cat walks a grid of letters collecting every drawing of the letter written on the mouse's door;
 * the door opens when the last one is in the train. Five doors, then the run ends on a word.
 *
 * Two things here are deliberate absences and neither may grow back:
 *  - **no clock** (§5.5), because the player reads a letter in three seconds one day and ten the
 *    next;
 *  - **no score**, anywhere, at any point.
 *
 * Running out of fish ends the run and goes back to the menu (§5.4) — it used to re-deal the board,
 * which played the treat on for ever with no way out but the ← button.
 */
export class CatScene extends Phaser.Scene {
  private lang = 'de';
  private startBoardNo = 1;
  private boardNo = 1;
  private classes: LetterClass[] = [];
  private board: CatBoard | null = null;

  private cat: Cell = [0, 0];
  private fish = CAT.startFish;
  private lostThisBoard = 0;
  /** Cell keys already collected on this board, in collection order. */
  private taken: string[] = [];
  /** The cat's recent cells, newest first — where the train walks (§5.2). */
  private history: Cell[] = [];
  private running = false;
  private over = false;
  /** A straight-line walk is playing out; taps wait rather than interleaving (§5.1.1). */
  private walking = false;
  /** Words this run has already spelled — no two picture doors of a run wear the same word (§7.1). */
  private usedWordIds = new Set<string>();

  private cell = 80;
  private gridX = 0;
  private gridY = 0;

  private tiles: { plate: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[][] = [];
  private catToken!: Phaser.GameObjects.Text;
  private trainIcons: Phaser.GameObjects.Text[] = [];
  private doorArt: {
    door: CatDoor; doorway: Phaser.GameObjects.Rectangle; panel: Phaser.GameObjects.Rectangle;
    label: Phaser.GameObjects.Text; mouse: Phaser.GameObjects.Text;
    icon: Phaser.GameObjects.Image | null;
  }[] = [];
  /** Every letter found: the board is answered, and §5.3's "the way out is open" is in force. */
  private cleared = false;
  /** The paw prints drawn from the cat to the nearest open door, once the board is cleared. */
  private wayOut: Phaser.GameObjects.Text[] = [];
  /** Board 1 only: rings on the cells the cat can step to right now (§4.1.1). */
  private moveHints: Phaser.GameObjects.Graphics | null = null;
  private fishIcons: Phaser.GameObjects.Text[] = [];
  private trainText!: Phaser.GameObjects.Text;
  /** The one sentence on the screen: what this board wants (§4.2.1). */
  private askText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'Cat' });
  }

  init(data: CatInit): void {
    this.lang = data.lang;
    this.startBoardNo = Math.min(Math.max(1, Math.floor(data.startBoard ?? 1)), CAT.boards);
    this.boardNo = this.startBoardNo;
    this.fish = CAT.startFish;
    this.lostThisBoard = 0;
    this.taken = [];
    this.history = [];
    this.tiles = [];
    this.doorArt = [];
    this.wayOut = [];
    this.cleared = false;
    this.trainIcons = [];
    this.board = null;
    this.running = false;
    this.over = false;
  }

  preload(): void {
    // The player's chosen backdrop, on demand — this mode has no exclusive backdrop either.
    const bg = getBackground();
    if (bg !== 'space') {
      const asset = backgroundAsset(bg);
      if (!this.textures.exists(`bg_${asset}`)) {
        this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
      }
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => stopBackdropAmbience());

    // A hand, not a crosshair: nothing here is aimed at, everything is tapped. Same as Pairs.
    this.input.setDefaultCursor('pointer');

    const pack = getLettersPack(this.lang) ?? getLettersPack('en');
    this.classes = pack ? classesOf(pack) : [];

    this.drawHud();
    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    this.showHowTo(() => this.startBoard(this.boardNo));
  }

  // ── boards ───────────────────────────────────────────────────────────────────

  private startBoard(n: number): void {
    this.boardNo = n;
    this.lostThisBoard = 0;
    this.fish = CAT.startFish;
    this.taken = [];
    this.cleared = false;
    this.walking = false;

    const progress = letterProgress(this.lang);
    const ranked = this.classes.map((c) => c.id);
    const board = dealBoard({
      board: n,
      classes: this.classes,
      activeIds: activeClassIds(progress, ranked),
      knownIds: new Set(ranked.filter((id) => isKnown(progress, id))),
      metIds: metClassIds(progress, ranked),
      lang: this.lang,
      picturePool: getPictureWordsPool(this.lang),
      usedWordIds: this.usedWordIds,
      rng: Math.random,
    });
    if (board.word) this.usedWordIds.add(board.word.id);
    this.board = board;
    this.cat = board.entry;
    this.history = [board.entry];

    // The word board's door wears a picture, so its icon has to be there before the board is drawn.
    const iconId = board.word?.id;
    if (iconId && !this.textures.exists(wordIconKey(iconId))) {
      this.load.image(wordIconKey(iconId), wordIconUrl(iconId));
      this.load.once(Phaser.Loader.Events.COMPLETE, () => this.layoutBoard(board));
      this.load.start();
      return;
    }
    this.layoutBoard(board);
  }

  private layoutBoard(board: CatBoard): void {
    this.clearBoard();

    // The grid keeps the left of the screen; the doors take the right margin, because a door the
    // cat walks THROUGH has to be outside the grid rather than drawn on its last column.
    const play = { x: 70, y: 150, w: 930, h: 500 };
    this.cell = Math.floor(Math.min(play.w / board.cols, play.h / board.rows, 132));
    this.gridX = Math.round(play.x + (play.w - this.cell * board.cols) / 2);
    this.gridY = Math.round(play.y + (play.h - this.cell * board.rows) / 2);

    this.askText.setText(this.askFor(board));

    const fontSize = Math.round(this.cell * 0.52);
    for (let r = 0; r < board.rows; r++) {
      const row: { plate: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
      for (let c = 0; c < board.cols; c++) {
        const { x, y } = this.cellCentre(c, r);
        // Floor reads as a path rather than as a tile with nothing on it: darker, thinner-edged and
        // part-transparent, so the lettered cells are visibly the things you stop at.
        const isFloor = board.grid[r][c] === '';
        const plate = this.add.rectangle(
          x, y, this.cell - 8, this.cell - 8, isFloor ? 0x2c3a46 : 0xf3f7fb, isFloor ? 0.55 : 1,
        ).setStrokeStyle(isFloor ? 2 : 3, isFloor ? 0x44586a : 0x9ab6d0).setDepth(10);
        const label = this.add.text(x, y, board.grid[r][c], {
          fontFamily: scriptFontFamily(this.lang), fontSize: `${fontSize}px`, color: '#22303c',
        }).setOrigin(0.5).setDepth(20);
        plate.setInteractive({ useHandCursor: true });
        plate.on('pointerdown', () => this.tryStep([c, r]));
        row.push({ plate, label });
      }
      this.tiles.push(row);
    }

    this.drawDoors(board);

    this.catToken = this.add.text(0, 0, '🐱', {
      fontFamily: FONT_STACK, fontSize: `${Math.round(this.cell * 0.6)}px`,
    }).setOrigin(0.5).setDepth(40);
    this.placeCat();
    this.paintFish();
    this.paintTrain();
    this.paintMoveHints();

    // The board arrives rather than appearing, corner-first — the same welcome the Ice Maze gives.
    this.tiles.flat().forEach((t, i) => {
      t.plate.setAlpha(0);
      t.label.setAlpha(0);
      this.tweens.add({ targets: [t.plate, t.label], alpha: 1, duration: 180, delay: i * 18 });
    });

    this.time.delayedCall(this.tiles.flat().length * 18 + 220, () => {
      if (this.over) return;
      this.running = true;
      this.speakAsk();
    });
  }

  /**
   * What this board wants, in one sentence — written across the top and spoken (§4.2.1).
   *
   * The door wearing the letters says *which* letters; it does not say what to DO with them, and a
   * four-year-old meeting the mode for the first time has no reason to guess "collect these, then
   * walk to the mouse". So the sentence is explicit, it names the letters inside itself rather than
   * making the child join two things up, and it is read aloud on every board because the player this
   * is built for cannot read it.
   *
   * The word boards get the other sentence, and it must say **in the right order** without saying
   * the word: the picture on the door is the question, and giving it away in text would answer it.
   */
  private askFor(board: CatBoard, spoken = false): string {
    if (board.word) return i18next.t('cat.findWord');
    // Two doors means two doors in the sentence as well. An explicit second key rather than
    // i18next pluralisation: `count` would pick the form from the number of LETTERS, and what
    // changes here is the number of doors.
    // The spoken sentence is NOT the written one with the letters swapped: the written line is built
    // around a compact "M m", and the spoken letters expand to a phrase — "Sammle alle grosses S und
    // kleines s" is not German. It gets its own, shorter phrasing, split in two, which suits the
    // audience better anyway.
    //
    // One key each, not two: there is exactly one door now (§4.4), so nothing here is ever plural.
    const key = spoken ? 'cat.askSpoken' : 'cat.findLetters';
    const letters = spoken
      ? board.classes.map((c) => spokenLetters(c)).join(', ')
      : board.doors.map((d) => d.label).join(' · ');
    return i18next.t(key, { letters });
  }

  /**
   * Say the ask — which is NOT the same string as the one on the screen.
   *
   * Written, a case pair is two letterforms side by side: `M m`. Read aloud that is two identical
   * letter names in a row, which tells the child nothing and sounds like a stutter. What a teacher
   * says is "grosses M und kleines m", and that is the whole lesson of the board said out loud.
   * The screen keeps the compact form, because there the two shapes ARE the point.
   */
  private speakAsk(): void {
    const board = this.board;
    if (!board) return;
    speakRulePrompt(this.askFor(board, true), this.lang);
  }

  private clearBoard(): void {
    for (const row of this.tiles) for (const t of row) { t.plate.destroy(); t.label.destroy(); }
    this.tiles = [];
    for (const d of this.doorArt) {
      d.doorway.destroy(); d.panel.destroy(); d.label.destroy(); d.mouse.destroy(); d.icon?.destroy();
    }
    this.doorArt = [];
    for (const t of this.trainIcons) t.destroy();
    this.trainIcons = [];
    for (const p of this.wayOut) p.destroy();
    this.wayOut = [];
    this.moveHints?.destroy();
    this.moveHints = null;
    this.catToken?.destroy();
  }

  private drawDoors(board: CatBoard): void {
    const x = this.gridX + this.cell * board.cols + 108;
    for (const door of board.doors) {
      const { y } = this.cellCentre(board.cols - 1, door.row);

      // The sign goes up FIRST, because the door is sized to it. A two-class board puts its two
      // pairs on two lines rather than one long row: at 58px bold, "M m · T t" is ~200px against a
      // 140px door, and it spilled straight across the board and over the mouse. Stacked, it also
      // reads as what it is — two pairs — which is the job the second door used to do (§4.4).
      let icon: Phaser.GameObjects.Image | null = null;
      let label: Phaser.GameObjects.Text;
      if (board.word) {
        // The word board's door wears the PICTURE, and no letters at all (§7.1).
        const key = wordIconKey(board.word.id);
        if (this.textures.exists(key)) {
          icon = this.add.image(x, y, key).setDepth(22);
          icon.setScale(Math.min(96 / icon.width, 96 / icon.height));
        }
        label = this.add.text(x, y, icon ? '' : board.word.text, {
          fontFamily: scriptFontFamily(this.lang), fontSize: '34px', color: '#fff4e2',
          align: 'center',
        }).setOrigin(0.5).setDepth(22);
      } else {
        const pairs = door.label.split(' · ');
        label = this.add.text(x, y, pairs.join('\n'), {
          fontFamily: scriptFontFamily(this.lang), fontSize: pairs.length > 1 ? '46px' : '58px',
          color: '#fff4e2', fontStyle: 'bold', align: 'center', lineSpacing: 6,
        }).setOrigin(0.5).setDepth(22);
      }

      const w = Math.max(140, Math.round(label.width) + 34, icon ? 140 : 0);
      const h = Math.max(
        Math.min(this.cell - 6, 150),
        Math.round(label.height) + 30,
        icon ? Math.round(icon.displayHeight) + 30 : 0,
      );
      // The opening behind the door, dark and empty. It is drawn from the first frame and hidden by
      // the door itself, so when the door swings there is somewhere to have swung *from*.
      const doorway = this.add.rectangle(x, y, w, h, 0x140f0a).setStrokeStyle(6, 0x5d3a19).setDepth(14)
        .setInteractive({ useHandCursor: true });
      // The doorway never moves or scales, so it stays a full-size hit area after the door swings —
      // unlike the panel, whose open tween shrinks its own. Tapping either still works; walking in
      // is what the child actually does.
      doorway.on('pointerdown', () => this.tryDoor(door));
      const panel = this.add.rectangle(x, y, w, h, 0x8b5a2b)
        .setStrokeStyle(6, 0x5d3a19).setDepth(15).setInteractive({ useHandCursor: true });
      panel.on('pointerdown', () => this.tryDoor(door));

      const mouse = this.add.text(x + w / 2 + 34, y, '🐭', {
        fontFamily: FONT_STACK, fontSize: '46px',
      }).setOrigin(0.5).setDepth(22);
      this.doorArt.push({ door, doorway, panel, label, mouse, icon });
    }
  }

  private cellCentre(col: number, row: number): { x: number; y: number } {
    return { x: this.gridX + this.cell * (col + 0.5), y: this.gridY + this.cell * (row + 0.5) };
  }

  // ── moving ───────────────────────────────────────────────────────────────────

  /**
   * A tap on a cell.
   *
   * A NON-ADJACENT tap does nothing at all — no flash, no sound, no penalty (§5.1). A four-year-old's
   * first instinct is to tap the letter they can see from across the board, and punishing that
   * instinct would be punishing them for having found the right answer.
   */
  private tryStep(target: Cell): void {
    const board = this.board;
    if (!board || !this.running || this.over || this.walking) return;

    // A tap anywhere in the cat's own row or column walks the whole line to it (§5.1.1); an
    // adjacent tap is just the one-cell case. The rules live in `cat/walk.ts`, Phaser-free, so a
    // test can drive a whole run through them (walk.test.ts).
    const route = lineSteps(board, this.walkState(), target);
    if (route.length === 0) return;
    this.walkRoute(route);
  }

  /** Walk the cells one at a time, so the child can see the cat cross the board. */
  private walkRoute(route: Cell[]): void {
    const board = this.board;
    if (!board) return;
    this.walking = route.length > 1;

    const take = (i: number): void => {
      if (i >= route.length || this.over || !this.running) { this.walking = false; return; }
      const outcome = step(board, this.walkState(), route[i]);
      switch (outcome.kind) {
        case 'ignored':
          break;
        case 'moved':
          this.moveTo(route[i]);
          break;
        case 'collected':
          this.collect(route[i], outcome.state.cleared);
          break;
        case 'wrong':
          this.wrongStep(route[i]);
          break;
      }
      if (i + 1 >= route.length) { this.walking = false; return; }
      this.time.delayedCall(110, () => take(i + 1));
    };
    take(0);
  }

  /** The scene's own fields as the pure rules see them. */
  private walkState(): WalkState {
    return { cat: this.cat, taken: this.taken, fish: this.fish, cleared: this.cleared };
  }

  private moveTo(target: Cell): void {
    this.cat = target;
    this.history.unshift(target);
    this.history = this.history.slice(0, CAT.wordCells * 2 + 2);
    this.placeCat();
    this.paintTrain();
    this.paintMoveHints();
    if (this.cleared) {
      this.paintWayOut();
      this.checkArrived();
    }
  }

  /**
   * Board 1 only: a ring on every cell the cat can step to from where it stands.
   *
   * The first board is the tutorial and it carries no text, so the one thing it cannot leave to
   * inference is *that the cat moves, and how far*. A child who has not worked out that taps move
   * the cat one cell at a time has no way in at all. The rings follow the cat and stop after board 1
   * — by then the rule has been shown three times over, and a permanent highlight would start doing
   * the looking for them.
   */
  private paintMoveHints(): void {
    this.moveHints?.destroy();
    this.moveHints = null;
    const board = this.board;
    if (!board || this.boardNo !== 1 || this.over || this.cleared) return;

    const g = this.add.graphics().setDepth(25);
    g.lineStyle(4, 0xffd166, 1);
    const size = this.cell - 18;
    for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const n: Cell = [this.cat[0] + dc, this.cat[1] + dr];
      if (n[0] < 0 || n[1] < 0 || n[0] >= board.cols || n[1] >= board.rows) continue;
      const { x, y } = this.cellCentre(n[0], n[1]);
      g.strokeRoundedRect(x - size / 2, y - size / 2, size, size, 10);
    }
    this.moveHints = g;
    this.tweens.add({ targets: g, alpha: { from: 0.3, to: 0.9 }, duration: 700, yoyo: true, repeat: -1 });
  }

  /**
   * Walking onto a door cell IS going through the door (§5.3.2).
   *
   * There is no second tap to find. The previous build needed one on the door graphic, and by the
   * time the door had opened that graphic was a ~20px sliver — the open tween scales the panel down
   * and Phaser keeps the hit area tied to the object, so the thing the child could see was not the
   * thing they could hit. Play testing reported it as "the door opens and I am stuck".
   */
  private checkArrived(): void {
    const board = this.board;
    if (!board || !this.running || this.over) return;
    const door = doorUnderCat(board, this.walkState());
    if (!door) return;
    if (doorOutcome(board, this.walkState(), door) !== 'open') return;
    this.openDoor(door);
  }

  private collect(target: Cell, cleared: boolean): void {
    const board = this.board!;
    const [c, r] = target;
    const tile = this.tiles[r][c];
    this.taken.push(cellKey(target));
    this.moveTo(target);

    // The glyph stands up and falls in behind the cat — the train IS the progress display (§5.2).
    // The cell it left behind becomes plain FLOOR, pixel for pixel: it used to stay pale, and a pale
    // cell under the cat's tail is unreadable — collected letter, or just the tail lying across a
    // letter still to take? An emptied cell and an empty cell are the same thing, so they look it.
    tile.label.setAlpha(0);
    tile.plate.setFillStyle(0x2c3a46, 0.55);
    tile.plate.setStrokeStyle(2, 0x44586a);
    playPopSound();
    speakRulePrompt(board.grid[r][c], this.lang);
    this.paintTrain();

    if (cleared) this.readyForDoor();
  }

  /**
   * A decoy. The cat **does not move** — the wrong cell is never entered (§5.2), so there is no
   * state to recover from and never a question about where the cat is. The letter yawns and rolls
   * over; it was asleep, it is not a buzzer.
   */
  private wrongStep(target: Cell): void {
    const [c, r] = target;
    const tile = this.tiles[r][c];
    playThudSound();
    this.tweens.add({
      targets: [tile.plate, tile.label], angle: { from: -8, to: 8 },
      duration: 90, yoyo: true, repeat: 1, onComplete: () => { tile.plate.angle = 0; tile.label.angle = 0; },
    });
    this.fish = Math.max(0, this.fish - 1);
    this.lostThisBoard++;
    this.paintFish();
    if (this.fish === 0) this.mouseGotAway();
  }

  /**
   * The last letter is in the train — the board has been answered (§5.3).
   *
   * Three things happen at once, and all three exist because a pulsing door was not enough: play
   * testing reported "the doors are blinking but it is not clear what to do", and worse, that the
   * way to a door could be **blocked by a letter** the child had no reason to step on.
   *
   *  1. **Every remaining letter falls asleep.** The question has been answered, so the rest of the
   *     board stops being a question: the decoys dim to floor and become free to walk. Nothing can
   *     stand between the child and the exit, which also closes the ordering hole in the deal-time
   *     route guarantee — that guarantee held for the order the board was dealt in, and a child
   *     collecting in a different order could strand themselves behind a decoy.
   *  2. **The doors swing open**, showing the doorway behind them, instead of merely pulsing.
   *  3. **Paw prints appear** from the cat to the nearest open door, and follow the cat as it walks.
   *
   * None of this can leak an answer: it only ever runs once every target is already collected.
   */
  private readyForDoor(): void {
    if (this.cleared) return;
    this.cleared = true;
    const board = this.board;
    if (!board) return;

    for (let r = 0; r < board.rows; r++) {
      for (let c = 0; c < board.cols; c++) {
        if (board.grid[r][c] === '') continue;
        if (this.taken.includes(cellKey([c, r]))) continue;
        const tile = this.tiles[r]?.[c];
        if (!tile) continue;
        this.tweens.add({ targets: tile.label, alpha: 0.18, duration: 420 });
        this.tweens.add({ targets: tile.plate, alpha: 0.55, fillAlpha: 0.55, duration: 420 });
        tile.plate.setFillStyle(0x2c3a46, 0.55);
      }
    }

    for (const d of this.doorArt) {
      this.tweens.add({
        targets: [d.panel, d.label, d.icon].filter(Boolean),
        scaleX: 0.14, x: d.panel.x - d.panel.width * 0.43, duration: 420, ease: 'Back.easeIn',
      });
      // The mouse steps into the opening and waves: the one thing on screen that is now moving.
      this.tweens.add({ targets: d.mouse, x: d.doorway.x, duration: 420, ease: 'Sine.easeOut' });
      this.tweens.add({
        targets: d.mouse, y: d.mouse.y - 8, duration: 420, yoyo: true, repeat: -1, delay: 420,
        ease: 'Sine.easeInOut',
      });
    }
    playGateSound();
    this.paintWayOut();
    // The last letter can itself sit on a door cell, in which case the cat is already there.
    this.time.delayedCall(420, () => this.checkArrived());
  }

  /** Paw prints from the cat to the nearest open door. Only ever drawn on a cleared board. */
  private paintWayOut(): void {
    for (const p of this.wayOut) p.destroy();
    this.wayOut = [];
    const board = this.board;
    if (!board || !this.cleared || this.over) return;

    // The very same route a tap on that door walks (§5.3.2) — the prints and the tap cannot disagree
    // about the way out, because there is only one answer to ask.
    let best: Cell[] | null = null;
    for (const d of board.doors) {
      const route = doorSteps(board, this.walkState(), d);
      if (route.length > 0 && (!best || route.length < best.length)) best = route;
    }
    if (!best) return;

    best.forEach((cell, i) => {
      const { x, y } = this.cellCentre(cell[0], cell[1]);
      const paw = this.add.text(x, y, '🐾', {
        fontFamily: FONT_STACK, fontSize: `${Math.round(this.cell * 0.3)}px`,
      }).setOrigin(0.5).setDepth(34).setAlpha(0);
      this.tweens.add({ targets: paw, alpha: 0.75, duration: 200, delay: i * 90 });
      this.wayOut.push(paw);
    });
  }

  /**
   * Reaching a door. Arriving early is **not an error and costs nothing** (§5.3): the mouse
   * squeaks, the door stays shut, the cat stands there. A child walking to the exit to see what
   * happens is checking, and checking is not a mistake.
   */
  private tryDoor(door: CatDoor): void {
    const board = this.board;
    if (!board || !this.running || this.over || this.walking) return;
    const outcome = doorOutcome(board, this.walkState(), door);
    if (outcome === 'shut') {
      const art = this.doorArt.find((d) => d.door === door);
      if (art) this.tweens.add({ targets: art.mouse, x: art.mouse.x + 10, duration: 70, yoyo: true, repeat: 2 });
      playThudSound();
      return;
    }
    if (outcome === 'open') { this.openDoor(door); return; }

    // Not standing at it. Once the door is OPEN, tapping it walks the cat there and through in one
    // go — along the paw prints, corner by corner, from wherever it happens to be standing. Arriving
    // is what opens it (§5.3.2), so there is nothing to do here but walk.
    //
    // The prints are the instruction, and before this they pointed somewhere a tap could not follow:
    // §5.1.1's line rule reaches the door only from its own row or column, so a cat one row off had
    // to be steered round the corner by hand while the way out was drawn in front of it.
    //
    // Only when it is open: a closed door is not somewhere the child is trying to get to yet, and
    // sending the cat across the board for a squeak would move it somewhere nobody asked for.
    if (!this.cleared) return;
    const route = doorSteps(board, this.walkState(), door);
    if (route.length === 0) return;
    this.walkRoute(route);
  }

  private openDoor(door: CatDoor): void {
    this.running = false;
    for (const p of this.wayOut) p.destroy();
    this.wayOut = [];
    const art = this.doorArt.find((d) => d.door === door);
    if (art) this.tweens.add({ targets: [art.panel, art.label], alpha: 0, x: art.panel.x + 60, duration: 420 });

    // The board's letters count toward the gated window — clean only if no fish was lost (§8.2).
    const drilled = this.board?.classes.map((c) => c.id) ?? [];
    const alsoSeen = this.board?.decoyClassIds ?? [];
    if (drilled.length > 0 || alsoSeen.length > 0) {
      recordBoard(this.lang, drilled, this.lostThisBoard === 0, alsoSeen);
    }

    const word = this.board?.word;
    if (word) speakRulePrompt(word.text, this.lang);

    this.tweens.add({
      targets: this.catToken, x: (art?.panel.x ?? this.catToken.x) + 40, alpha: 0, duration: 520,
      onComplete: () => {
        if (this.boardNo >= CAT.boards) this.endRun();
        else { playRoundCompleteSound(); this.startBoard(this.boardNo + 1); }
      },
    });
  }

  /**
   * Out of fish: the mouse gets away and **the run is over** (§5.4). The message holds, then the
   * scene goes back to the menu by itself — a tap after a moment leaves sooner. No summary screen
   * and no number, same as a finished run.
   */
  private mouseGotAway(): void {
    if (this.over) return;
    this.over = true;
    this.running = false;
    playThudSound();
    const msg = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, i18next.t('cat.mouseGotAway'), {
      fontFamily: FONT_STACK, fontSize: '54px', color: '#ffd166', align: 'center',
      padding: { x: 30, y: 18 },
    }).setOrigin(0.5).setDepth(120).setAlpha(0);
    const bg = addRoundedLabelBg(this, msg, { radius: 22 });
    bg.setAlpha(0);
    this.tweens.add({ targets: [msg, bg], alpha: 1, duration: 240 });

    const shownAt = performance.now();
    let gone = false;
    const leave = (): void => {
      if (gone) return;
      gone = true;
      this.input.off('pointerdown', tap);
      this.scene.start('Menu');
    };
    const tap = (): void => { if (performance.now() - shownAt >= 900) leave(); };
    this.input.on('pointerdown', tap);
    this.time.delayedCall(CAT.gotAwayToMenuMs, leave);
  }

  // ── the run ──────────────────────────────────────────────────────────────────

  private endRun(): void {
    if (this.over) return;
    this.over = true;
    this.running = false;
    playJourneyFanfare();

    const word = this.board?.word;
    const headline = word
      ? i18next.t('cat.wordDone', { word: word.text })
      : i18next.t('cat.runDone');
    const msg = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, `🐱🐭\n${headline}`, {
      fontFamily: scriptFontFamily(this.lang), fontSize: '56px', color: '#4ecdc4',
      align: 'center', padding: { x: 34, y: 20 }, lineSpacing: 10,
    }).setOrigin(0.5).setDepth(120).setAlpha(0).setScale(0.85);
    const bg = addRoundedLabelBg(this, msg, { radius: 24 });
    bg.setAlpha(0).setScale(0.85);
    this.tweens.add({ targets: [msg, bg], alpha: 1, scale: 1, duration: 320, ease: 'Back.easeOut' });

    // No summary screen and no number. Every other treat ends on a score; this one ends on a word,
    // and `4/5 boards` under it would be the cheapest possible way to spoil that (§11).
    const shownAt = performance.now();
    const leave = (): void => {
      if (performance.now() - shownAt < 900) return;
      this.input.off('pointerdown', leave);
      this.scene.start('Menu');
    };
    this.input.on('pointerdown', leave);
    maybeShowBuyPrompt(this, { delayMs: 1800 });
  }

  // ── painting ─────────────────────────────────────────────────────────────────

  private placeCat(): void {
    const { x, y } = this.cellCentre(this.cat[0], this.cat[1]);
    this.tweens.add({ targets: this.catToken, x, y, duration: 140, ease: 'Sine.easeOut' });
  }

  /**
   * The train: every collected letter walking one cell behind the cat, in collection order.
   *
   * This is the progress display, and it is on the board rather than in a bar under it — it keeps
   * the child's eyes where the letters are, and it makes the collected letters characters rather
   * than entries in a list (§5.2).
   */
  private paintTrain(): void {
    const board = this.board;
    if (!board) return;
    const size = Math.round(this.cell * 0.42);
    while (this.trainIcons.length < this.taken.length) {
      // Dark glyph, light stroke: a train letter crosses both the dark floor and the pale tile it
      // was lifted off, and plain white on pale read as nothing at all.
      this.trainIcons.push(this.add.text(0, 0, '', {
        fontFamily: scriptFontFamily(this.lang), fontSize: `${size}px`, color: '#22303c',
        fontStyle: 'bold', stroke: '#f7fbff', strokeThickness: 6,
      }).setOrigin(0.5).setDepth(38));
    }
    this.taken.forEach((key, i) => {
      const [c, r] = key.split(',').map(Number);
      const icon = this.trainIcons[i];
      icon.setFontSize(size);
      icon.setText(board.grid[r][c]);
      // history[0] is the cat itself, so the first letter walks in history[1].
      const at = this.history[Math.min(i + 1, this.history.length - 1)] ?? this.cat;
      const { x, y } = this.cellCentre(at[0], at[1]);
      this.tweens.add({ targets: icon, x, y, duration: 160, ease: 'Sine.easeOut' });
    });

    // The word so far, spelled out under the grid — and in Korean, assembled (§7.3).
    if (board.word) {
      this.trainText.setText(renderTrain(board.word, this.taken.length));
    } else {
      this.trainText.setText('');
    }
  }

  private paintFish(): void {
    this.fishIcons.forEach((f, i) => f.setAlpha(i < this.fish ? 1 : 0.25));
  }

  private drawHud(): void {
    // The ask takes the top row, where the mode's own title used to sit: inside the mode the title
    // says nothing the child needs, and this is the only sentence on the screen. It uses the
    // language's own script font because it has the letterforms embedded in it.
    this.askText = this.add.text(GAME_WIDTH / 2, 52, '', {
      fontFamily: scriptFontFamily(this.lang), fontSize: '30px', color: '#ffd166',
      align: 'center', wordWrap: { width: 860 },
    }).setOrigin(0.5).setDepth(50);
    // Tapping it says it again — the escape hatch for a child who missed it, the same one the Ice
    // Maze banner offers.
    this.askText.setInteractive({ useHandCursor: true });
    this.askText.on('pointerdown', () => this.speakAsk());

    this.fishIcons = [];
    for (let i = 0; i < CAT.startFish; i++) {
      this.fishIcons.push(this.add.text(GAME_WIDTH - 60 - i * 52, 52, '🐟', {
        fontFamily: FONT_STACK, fontSize: '38px',
      }).setOrigin(0.5).setDepth(50));
    }

    this.trainText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 42, '', {
      fontFamily: scriptFontFamily(this.lang), fontSize: '46px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(50);

    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(-back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm — the decision Pairs settled and every treat has kept.
    back.on('pointerdown', () => this.scene.start('Menu'));
  }

  private drawBackdrop(): void {
    const bg = getBackground();
    if (bg === 'space') { new Starfield(this); return; }
    const key = `bg_${backgroundAsset(bg)}`;
    if (!this.textures.exists(key)) { new Starfield(this); return; }
    const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key).setDepth(-10);
    const scale = Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height);
    img.setScale(scale).setAlpha(0.55);
  }

  // ── overlays ─────────────────────────────────────────────────────────────────

  private showHowTo(onStart: () => void): void {
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
    shade.setInteractive();
    const panel = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.8, GAME_HEIGHT * 0.7, 0x1a1f25)
      .setStrokeStyle(4, 0xffd166);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 180, `🐱  ${i18next.t('cat.title')}  🐭`, {
      fontFamily: FONT_STACK, fontSize: '58px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, i18next.t('cat.help'), {
      fontFamily: FONT_STACK, fontSize: '30px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.66 }, lineSpacing: 10,
    }).setOrigin(0.5);
    const fish = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 90, '🐟'.repeat(CAT.startFish), {
      fontFamily: FONT_STACK, fontSize: '40px',
    }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 190, i18next.t('cat.start'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#22303c', backgroundColor: '#ffd166',
      padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, fish, start]);

    // Tap anywhere to start, behind a TIMESTAMP guard so the tap that spent the Cat cannot skip the
    // card it just opened. Same as Pairs §8.1 and the Ice Maze.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }
}

/**
 * How a letter class is SPOKEN: "grosses M und kleines m" where the language has case, and the
 * plain label where it does not.
 *
 * Whether a class has case is read off the data rather than off a list of languages: a class has it
 * when its first two forms are the same letter in the two cases. That is true of `M`/`m` and of
 * `Ä`/`ä`, and false of Japanese `か`/`カ` (two scripts, not two cases), of Korean and Thai
 * singletons, and of German `ß`, which has only one form — all of which correctly fall back to
 * saying the glyph.
 */
function spokenLetters(cls: LetterClass): string {
  const [upper, lower] = cls.forms;
  const hasCase = !!upper && !!lower && upper !== lower && upper.toLowerCase() === lower;
  return hasCase ? i18next.t('cat.caseNames', { upper, lower }) : cls.label;
}

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  return bg === 'fable' ? fableVariant() : bg;
}
